/**
 * Custom hook for managing step navigation and progression in StudentDashboard
 * Enhanced version that uses configurable constants and templates
 */

import { useMemo } from 'react';
import {
  STEP_STATUS
} from '../utils/constants';
import {
  getTimelineSteps
} from '../utils/stepDefinitions';

/**
 * Maps application data to step progression state
 * @param {Object} applicationData - Application data from API
 * @param {boolean} showPreferencesForm - Whether preferences form should be shown
 * @returns {Object} Step progression information
 */
const getStepProgression = (applicationData, showPreferencesForm) => {
  const timelineSteps = getTimelineSteps(applicationData);

  if (!timelineSteps || timelineSteps.length === 0) {
    return {
      currentStep: 0,
      steps: [],
      totalSteps: 0
    };
  }

  // Find the current active step
  const activeStepIndex = timelineSteps.findIndex(step => step.isActive);
  const currentStep = activeStepIndex >= 0 ? activeStepIndex : 0;

  // Map timeline steps to navigation-friendly format with template information
  const steps = timelineSteps.map(step => ({
    id: step.id,
    title: step.title,
    status: step.status,
    isActive: step.isActive,
    content: step.content,
    actions: step.actions || [],
    // Apply showPreferencesForm UI state to scholars-invitation step only
    showPreferencesForm: step.id === 'scholars-invitation' ? showPreferencesForm : false,
    // Enhanced V2 features - use templateType from step definition
    templateType: step.templateType || 'default',
    showCondensed: step.status === STEP_STATUS.COMPLETE && !step.isActive
  }));

  return {
    currentStep,
    steps,
    totalSteps: steps.length
  };
};



/**
 * Hook for managing step navigation state in StudentDashboard
 * @param {Object} applicationData - Application data from useStudentDashboardData
 * @returns {Object} Step navigation state and helper functions
 */
export const useStepNavigation = (applicationData = {}, showPreferencesForm = false) => {

  // Calculate step progression
  const progression = useMemo(() => {
    return getStepProgression(applicationData, showPreferencesForm);
  }, [applicationData, showPreferencesForm]);

  // Helper functions for step navigation
  const getActiveStep = () => {
    return progression.currentStep;
  };

  const getStepByIndex = (stepIndex) => {
    return progression.steps[stepIndex] || null;
  };

  const getStepById = (stepId) => {
    return progression.steps.find(step => step.id === stepId) || null;
  };

  const getStepStatus = (stepIndex) => {
    const step = getStepByIndex(stepIndex);
    return step?.status || STEP_STATUS.PENDING;
  };

  const isStepComplete = (stepIndex) => {
    return getStepStatus(stepIndex) === STEP_STATUS.COMPLETE;
  };

  const isStepActive = (stepIndex) => {
    const step = getStepByIndex(stepIndex);
    return step?.isActive || false;
  };

  const isStepPending = (stepIndex) => {
    return getStepStatus(stepIndex) === STEP_STATUS.PENDING;
  };

  const getTotalSteps = () => {
    return progression.totalSteps;
  };

  const getCompletedStepsCount = () => {
    return progression.steps.filter(step => step.status === STEP_STATUS.COMPLETE).length;
  };



  // Check if application has reached final step
  const hasReachedFinalStep = () => {
    const totalSteps = getTotalSteps();
    const activeStep = getActiveStep();
    const lastStepIndex = totalSteps - 1;

    return activeStep >= lastStepIndex || isStepComplete(lastStepIndex);
  };

  // Get the next step that needs attention
  const getNextStep = () => {
    const nextStepIndex = progression.steps.findIndex(step =>
      step.status === STEP_STATUS.IN_PROGRESS || step.status === STEP_STATUS.PENDING
    );

    return nextStepIndex >= 0 ? nextStepIndex : null;
  };

  // Check if all steps are complete
  const isApplicationComplete = () => {
    return progression.steps.every(step => step.status === STEP_STATUS.COMPLETE);
  };

  // Get steps that should be visible to the student (V2 enhanced)
  const getVisibleSteps = () => {
    // Check if future steps should be shown (for development/testing)
    const showFutureSteps = process.env.REACT_APP_ENABLE_FUTURE_STEPS === 'true';

    let stepsToShow = progression.steps;

    if (!showFutureSteps) {
      // Only show completed steps and active/in-progress steps
      stepsToShow = progression.steps.filter(step =>
        step.status === STEP_STATUS.COMPLETE ||
        step.status === STEP_STATUS.IN_PROGRESS ||
        step.isActive
      );
    }

    return stepsToShow.map(step => ({
      ...step,
      // Mark completed steps to show in condensed form
      showCondensed: step.status === STEP_STATUS.COMPLETE && !step.isActive,
      // Mark future/pending steps to show in grayed out form (only when showing future steps)
      showGrayedOut: showFutureSteps && step.status === STEP_STATUS.PENDING && !step.isActive
    }));
  };

  // Get contextual message for current state
  const getCurrentStateMessage = () => {
    const activeStep = getStepByIndex(getActiveStep());

    if (!activeStep) {
      return 'Your application is being processed.';
    }

    // Return the main message from the active step's content
    return activeStep.content?.message || activeStep.content?.description || 'Please check your application status.';
  };

  // Get next action the student should take
  const getNextAction = () => {
    const activeStep = getStepByIndex(getActiveStep());

    if (!activeStep || !activeStep.actions || activeStep.actions.length === 0) {
      return null;
    }

    // Return the primary action (usually the first one)
    return activeStep.actions[0];
  };

  // Check if step has preferences form
  const shouldShowPreferencesForm = (stepIndex) => {
    const step = getStepByIndex(stepIndex);
    return step?.showPreferencesForm || false;
  };

  return {
    // Core progression data
    progression,

    // Helper functions
    getActiveStep,
    getStepByIndex,
    getStepById,
    getStepStatus,
    isStepComplete,
    isStepActive,
    isStepPending,
    getTotalSteps,
    getCompletedStepsCount,

    hasReachedFinalStep,
    getNextStep,
    isApplicationComplete,
    getVisibleSteps,
    getCurrentStateMessage,
    getNextAction,
    shouldShowPreferencesForm,

    // Convenience getters for current state
    activeStepIndex: getActiveStep(),
    activeStep: getStepByIndex(getActiveStep()),
    visibleSteps: getVisibleSteps(),
    currentMessage: getCurrentStateMessage(),
    nextAction: getNextAction(),

    isComplete: isApplicationComplete(),

    // All steps (for admin/debug views)
    allSteps: progression.steps
  };
};

export default useStepNavigation;